import { ActionTree } from 'vuex';
import { PostgradTrialState } from './state';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';
import RootState from '@/store/root_state';
import { getDiffTime } from '@/helpers/date';
import { getChimeraToken } from '@/helpers/get-tokens';
import TrialRequestService from '@/helpers/postgradTrial/request';
import { PostgradTrial } from '@/models/postgradTrial/PostgradTrialModel';

interface CachedTrialsByCourseId {
  [descomplicaCourseId: string]: PostgradTrial;
}

const TRIAL_CONTRACT_DURATION_IN_DAYS = 7;
const LOCAL_STORAGE_KEY = 'postgradCoursesTrial';

const getCachedTrials = (): CachedTrialsByCourseId | null => {
  const cachedTrials = localStorage.getItem(LOCAL_STORAGE_KEY);

  return cachedTrials ? JSON.parse(cachedTrials) : null;
};

const getCachedTrialByCourseId = (descomplicaCourseId: string): PostgradTrial | null => {
  const cachedTrialsByCourseId = getCachedTrials();
  if (!cachedTrialsByCourseId) {
    return null;
  }

  return cachedTrialsByCourseId[descomplicaCourseId];
};

const cacheTrialByCourseId = (descomplicaCourseId: string, trial: PostgradTrial): void => {
  const cachedTrialsByCourseId = getCachedTrials();
  if (!cachedTrialsByCourseId) {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ [descomplicaCourseId]: trial }));

    return;
  }

  cachedTrialsByCourseId[descomplicaCourseId] = trial;
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cachedTrialsByCourseId));
};

const actions: ActionTree<PostgradTrialState, RootState> = {
  async GET_POSTGRAD_TRIAL({ commit }, chimeraCourse: ChimeraCourseModel | null) {
    const token = getChimeraToken();
    const contract = chimeraCourse?.contractByUser;

    if (!token || !chimeraCourse || !contract) return;

    const diffInDays = getDiffTime(contract.expirationDate, contract.startDate).days;
    if (diffInDays > TRIAL_CONTRACT_DURATION_IN_DAYS) {
      return;
    }

    const cachedTrial = getCachedTrialByCourseId(chimeraCourse._id);
    if (cachedTrial) {
      cachedTrial.startDate = new Date(cachedTrial.startDate);
      cachedTrial.expirationDate = new Date(cachedTrial.expirationDate);

      commit('SET_POSTGRAD_TRIAL_FOR_COURSE', {
        descomplicaCourseId: chimeraCourse._id,
        trial: cachedTrial,
      });

      return;
    }

    const trial = await TrialRequestService.getTrial(token, chimeraCourse._id);
    if (!trial) return;

    cacheTrialByCourseId(chimeraCourse._id, trial);

    commit('SET_POSTGRAD_TRIAL_FOR_COURSE', {
      descomplicaCourseId: chimeraCourse._id,
      trial,
    });
  },
};

export default actions;
